
import { defineComponent } from 'vue';
import BizIcon from '@/components/base/BizIcon.vue';
import BizTooltip from './BizTooltip.vue';

export default defineComponent({
    components: { BizIcon, BizTooltip },
    props: {
        type: {
            type: String,
            default: 'icon',
            validator: (value: string) => ['icon', 'label'].includes(value)
        },
        show: {
            type: Boolean,
            required: true
        },
        hasCaption: {
            type: Boolean,
            required: false
        },
        position: {
            type: String,
            default: 'top-left',
            validator: (value: string) =>
                ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(value)
        },
        positionMobile: {
            type: String,
            default: '',
            validator: (value: string) =>
                value === '' ||
                ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(value)
        },
        zIndex: {
            type: String,
            default: 'z-10'
        },
        text: {
            type: String,
            default: 'AI Generated'
        },
        iconSize: {
            type: String,
            default: 'w-4 h-4'
        },
        textSize: {
            type: String,
            default: 'text-sm'
        },
        textColor: {
            type: String,
            default: 'text-gray-700'
        },
        padding: {
            type: String,
            default: 'py-1'
        },
        iconColor: {
            type: String,
            default: 'text-black'
        },
        tooltipText: {
            type: String,
            default: 'This content was generated by AI'
        },
        tooltipPosition: {
            type: String,
            default: 'top',
            validator: (value: string) => ['top', 'bottom', 'left', 'right'].includes(value)
        }
    },
    computed: {
        isAiGenerated(): boolean {
            return this.show;
        },
        iconContainerClasses(): string {
            // Mobile positions (no prefix)
            const positions: Record<string, string> = {
                'top-left': 'top-4 left-4',
                'top-right': 'top-4 right-4',
                'bottom-left': this.hasCaption ? 'bottom-11 left-4' : 'bottom-4 left-4',
                'bottom-right': this.hasCaption ? 'bottom-11 right-4' : 'bottom-4 right-4'
            };

            // Desktop positions (lg: prefix)
            const desktopPositions: Record<string, string> = {
                'top-left': 'lg:top-4 lg:left-4 lg:bottom-auto lg:right-auto',
                'top-right': 'lg:top-4 lg:right-4 lg:bottom-auto lg:left-auto',
                'bottom-left': this.hasCaption
                    ? 'lg:bottom-11 lg:left-4 lg:top-auto lg:right-auto'
                    : 'lg:bottom-4 lg:left-4 lg:top-auto lg:right-auto',
                'bottom-right': this.hasCaption
                    ? 'lg:bottom-11 lg:right-4 lg:top-auto lg:left-auto'
                    : 'lg:bottom-4 lg:right-4 lg:top-auto lg:left-auto'
            };

            // If positionMobile is provided, use it for mobile and position for desktop
            // If not provided, use position for both
            const mobilePos = this.positionMobile || this.position;
            const desktopPos = this.position;

            const mobileClasses = positions[mobilePos] || positions['top-left'];
            const desktopClasses = desktopPositions[desktopPos] || desktopPositions['top-left'];

            return `absolute ${this.zIndex} ${mobileClasses} ${desktopClasses}`;
        },
        labelClasses(): string {
            return `inline-flex items-center gap-1.5 ${this.textColor} ${this.padding} ${this.textSize}`;
        }
    }
});
